import { useCallback, useEffect, useState } from 'react';
import { PageHeader, EmptyState, TableSkeleton } from '../components/common/PageHeader';
import { SearchFilterBar } from '../components/common/SearchFilterBar';
import { Modal } from '../components/common/Modal';
import CategoryForm, {
  emptyCategoryForm,
  formToCategoryFormData,
  categoryToForm,
} from '../components/forms/CategoryForm';
import { listCategories, createCategory, updateCategory } from '../services/categoryService';
import { getErrorMessage } from '../services/apiClient';
import { useToast } from '../context/ToastContext';
import { formatDate } from '../utils/format';

export default function CategoriesPage() {
  const { showToast } = useToast();
  const [categories, setCategories] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyCategoryForm);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listCategories();
      setCategories(res.data || []);
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  useEffect(() => {
    if (!search.trim()) {
      setFiltered(categories);
    } else {
      const q = search.toLowerCase();
      setFiltered(categories.filter((c) => c.name.toLowerCase().includes(q) || c.slug.toLowerCase().includes(q)));
    }
  }, [categories, search]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyCategoryForm);
    setModalOpen(true);
  };

  const openEdit = (category) => {
    setEditing(category);
    setForm(categoryToForm(category));
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      showToast('Name is required', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = formToCategoryFormData(form);
      if (editing) {
        await updateCategory(editing._id, payload);
        showToast('Category updated successfully');
      } else {
        await createCategory(payload);
        showToast('Category created successfully');
      }
      setModalOpen(false);
      fetchCategories();
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Categories"
        description="Organize products into categories"
        action={<button type="button" className="btn btn-primary" onClick={openCreate}>+ Add Category</button>}
      />

      <div className="card">
        <SearchFilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search categories..."
          onClear={() => setSearch('')}
        />

        {loading ? (
          <TableSkeleton cols={4} />
        ) : filtered.length === 0 ? (
          <EmptyState title="No categories found" action={{ label: 'Add Category', onClick: openCreate }} />
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Slug</th>
                  <th>Description</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((cat) => (
                  <tr key={cat._id}>
                    <td>
                      <div className="cell-product">
                        {cat.image && <img src={cat.image} alt="" className="cell-thumb" />}
                        <span className="cell-title">{cat.name}</span>
                      </div>
                    </td>
                    <td>{cat.slug}</td>
                    <td className="cell-truncate">{cat.description || '—'}</td>
                    <td>{formatDate(cat.createdAt)}</td>
                    <td>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => openEdit(cat)}>Edit</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Edit Category' : 'Create Category'}
        size="md"
      >
        <form onSubmit={handleSave}>
          <CategoryForm form={form} onChange={setForm} />
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={() => setModalOpen(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
